/**
 * The one way to PUT bytes at a presigned storage URL.
 *
 * Every presign flow in this client (tenant logo, employee photo, tenant
 * branding on the platform side, employee documents, punch photos) ends in the
 * same PUT, and each one had drifted into its own slightly different version of
 * it. Two of those differences were bugs, so the rule now lives here and the
 * call sites route through it.
 *
 * Two things this has to get right:
 *
 * 1. **No credentials, ever.** `fetch` defaults to `credentials: "same-origin"`.
 *    In dev the URL below is rewritten to a RELATIVE path so the Vite proxy can
 *    carry it — which makes it same-origin, which means the session cookies
 *    (`Path=/`) would ride along to the storage endpoint. Explicit `"omit"` is
 *    the fix, and it is also what S3/R2 require: an Authorization header or
 *    cookie alongside a presigned signature makes them reject the request.
 *
 * 2. **Rewrite only the dev backend's own storage URLs.** A prefix test on the
 *    origin string alone (`startsWith("http://localhost:8080")`) also matches
 *    `http://localhost:8080.example.com/...`, which would turn an attacker-
 *    controlled absolute URL into a same-origin request. The origin is compared
 *    exactly and the path has to be under `/dev-storage/` before anything is
 *    rewritten; a real R2 URL passes through untouched.
 */

/** The dev backend's own origin. Production storage is never on this host. */
const DEV_BACKEND_ORIGIN = "http://localhost:8080";

/** Only this prefix is ever proxied — see each app's `vite.config.ts`. */
const DEV_STORAGE_PATH = "/dev-storage/";

/**
 * Returns the URL to PUT to: a relative path when this is the dev backend's
 * own `/dev-storage/` URL, otherwise the input unchanged.
 *
 * Exported for the unit test; call `putToPresignedUrl` rather than this.
 */
export function toSameOriginDevStorage(uploadUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(uploadUrl, DEV_BACKEND_ORIGIN);
  } catch {
    // Not a URL we can reason about — hand it back and let fetch fail loudly
    // rather than silently rewriting something we do not understand.
    return uploadUrl;
  }
  // `origin` is parsed, not string-matched, so a lookalike host like
  // `localhost:8080.example.com` compares unequal and passes through.
  if (parsed.origin !== DEV_BACKEND_ORIGIN) return uploadUrl;
  if (!parsed.pathname.startsWith(DEV_STORAGE_PATH)) return uploadUrl;
  return `${parsed.pathname}${parsed.search}`;
}

export interface PresignedUploadOptions {
  /**
   * MUST match the `contentType` sent to the presign call — the signature
   * covers `content-type;host`, so a mismatch makes storage reject the upload.
   */
  contentType: string;
  /** Extra headers the presign response said are part of the signature. */
  requiredHeaders?: Record<string, string> | undefined;
}

/**
 * Header names a presign response may contribute. Everything else is dropped.
 *
 * An allowlist rather than a denylist because `requiredHeaders` comes off the
 * wire: a compromised or buggy presign response naming `Authorization` or
 * `Cookie` would otherwise smuggle the session onto the storage request, which
 * is the very thing `credentials: "omit"` above exists to prevent. Storage
 * providers only ever sign `content-type` plus their own vendor-prefixed
 * headers, so nothing legitimate is lost.
 */
function isAllowedUploadHeader(name: string): boolean {
  const lower = name.toLowerCase();
  if (lower === "content-md5" || lower === "content-encoding") return true;
  return lower.startsWith("x-amz-") || lower.startsWith("x-goog-") || lower.startsWith("x-ms-");
}

/**
 * Builds the header set actually sent: the allowed signed headers, then
 * `Content-Type` set exactly once.
 *
 * Case matters here. `{ "content-type": a }` spread alongside `"Content-Type": b`
 * yields two entries that `fetch` combines into `a, b`, which no signature
 * matches. Normalizing first means the caller's `contentType` always wins and
 * always appears once.
 *
 * Exported for the unit test; call `putToPresignedUrl` rather than this.
 */
export function buildUploadHeaders(
  contentType: string,
  requiredHeaders?: Record<string, string> | undefined,
): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(requiredHeaders ?? {})) {
    if (isAllowedUploadHeader(name)) headers[name.toLowerCase()] = value;
  }
  headers["content-type"] = contentType;
  return headers;
}

/**
 * PUT the bytes. Resolves with the raw `Response` so callers can map failures
 * into whatever error type they already use.
 */
export async function putToPresignedUrl(
  uploadUrl: string,
  body: Blob | File,
  { contentType, requiredHeaders }: PresignedUploadOptions,
): Promise<Response> {
  return fetch(toSameOriginDevStorage(uploadUrl), {
    method: "PUT",
    // Never inherit the ambient session — see the note at the top of the file.
    credentials: "omit",
    headers: buildUploadHeaders(contentType, requiredHeaders),
    body,
  });
}
